import {
  Call,
  Close as CloseIcon,
  Email,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CustomTextField from "../../../components/common/Textfield";
import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string | null;
  profile_picture_url: string | null;
}

interface UsersResponse {
  count: number;
  results: User[];
}

interface AssignMemoryBookModalProps {
  open: boolean;
  onClose: () => void;
  onAssign: (user: User, comments: string) => Promise<void>;
  //streamId?: number;
  currentAssignee?: {
    id: number;
    name: string;
    email?: string;
    avatarUrl?: string;
  } | null;
  initialCollectedComments?: string;
}

const AssignMemoryBookModal: React.FC<AssignMemoryBookModalProps> = ({
  open,
  onClose,
  onAssign,
  //streamId,
  currentAssignee,
  initialCollectedComments = "",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [assigningUserId, setAssigningUserId] = useState<number | null>(null);

  const [collectedComments, setCollectedComments] = useState<string>("");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isAssigned, setIsAssigned] = useState(false);

  const fetchSalesUsers = useCallback(async () => {
    setLoading(true);
    try {
      const url =
        searchTerm.trim().length >= 3
          ? `/api/authentication/users/?search=${encodeURIComponent(searchTerm)}`
          : "/api/authentication/users/";
      const response = await apiService.get<UsersResponse>(url);

      const mappedUsers: User[] = response.data.results.map((user) => ({
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone_number: user.phone_number,
        profile_picture_url: user.profile_picture_url,
      }));

      setFilteredUsers(mappedUsers);
    } catch {
      toast.error("Failed to load staff members");

      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (open) {
      setCollectedComments(initialCollectedComments);
      setIsAssigned(!!initialCollectedComments);
      fetchSalesUsers();
    }
  }, [open, initialCollectedComments, fetchSalesUsers]);


  useEffect(() => {
    if (!open) return;

    if (searchTerm.trim().length < 3) {
      if (searchTerm.trim().length === 0) {
        fetchSalesUsers();
      }
      return;
    }

    const debounce = setTimeout(() => {
      fetchSalesUsers();
    }, 400);

    return () => clearTimeout(debounce);
  }, [searchTerm, open, fetchSalesUsers]);



  const handleAssign = async (user: User) => {
    const commentsCount = parseInt(collectedComments, 10);
    if (isNaN(commentsCount) || commentsCount < 0) {
      toast.error("Please enter a valid number of comments");
      return;
    }

    try {
      setAssigningUserId(user.id);

      await new Promise((resolve) => setTimeout(resolve, 1000));

      await onAssign(user, collectedComments);
      setIsAssigned(true);
      toast.success(
        `Memory book assigned to ${user.first_name} ${user.last_name} with ${commentsCount} collected comments!`,
      );
      handleClose();
    } catch {
      toast.error("Failed to assign memory book");
    } finally {
      setAssigningUserId(null);
    }
  };

  const handleSelectedUser = (user: User) => {
    setSelectedUserId(user.id);
  };

  const handleClose = () => {
    setSearchTerm("");
    setFilteredUsers([]);
    setSelectedUserId(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: "90vh" } }}
    >
      <DialogTitle
        sx={{ p: 2, display: "flex", justifyContent: "space-between" }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Assign Memory Book
          </Typography>
          {currentAssignee && (
            <Typography variant="caption" color="text.secondary">
              Currently assigned to: {currentAssignee.name}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <CustomTextField
          fullWidth
          placeholder="Search sales team member..."
          helperText="Type at least 3 characters to search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
        />

        <Box
          sx={{
            mb: 3,
            p: 2,
          }}
        >
          <CustomTextField
            fullWidth
            type="number"
            label="Number of Collected Comments"
            placeholder="Enter number of collected comments"
            value={collectedComments}
            onChange={(e) => setCollectedComments(e.target.value)}
            helperText="How many physical comments were collected?"
            InputProps={{ inputProps: { min: 0 } }}
            //disabled={isAssigned}
          />
        </Box>

        {loading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" mt={2}>
              Loading...
            </Typography>
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              {searchTerm.trim().length >= 3
                ? "No sales team members found matching your search"
                : searchTerm.trim().length > 0
                  ? "Type at least 3 characters to search"
                  : "No sales team members available"}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredUsers.map((user) => {
              const isCurrentAssignee = currentAssignee?.id === user.id;
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={user.id}>
                  <Box
                    onClick={() => handleSelectedUser(user)}
                    sx={{
                      backgroundColor: reduxTheme.primary_colors[800],
                      p: 2,
                      borderRadius: 2,
                      border:
                        selectedUserId === user.id
                          ? `3px solid ${reduxTheme.primary_colors[400]}`
                          : isCurrentAssignee
                            ? `2px solid ${reduxTheme.primary_colors[400]}`
                            : "1px solid transparent",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        border: `2px solid ${reduxTheme.primary_colors[500]}`,
                      },
                    }}
                  >
                    {/* Top section */}
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      gap={1}
                    >
                      <Avatar
                        src={user?.profile_picture_url || "/placeholder.svg"}
                        sx={{ width: 64, height: 64 }}
                      />
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <Typography variant="subtitle2" textAlign="center">
                          {user?.first_name} {user?.last_name}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Bottom section */}
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      mt={2}
                    >
                      {/* {isCurrentAssignee && (
                        <Chip
                          label="Currently Assigned"
                          size="small"
                          color="primary"
                          sx={{ my: 1, borderRadius: 20 }}
                        />
                      )} */}

                      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                        {/* Phone */}
                        <Tooltip
                          title={user?.phone_number || "No phone number"}
                        >
                          <IconButton
                            disabled={!user?.phone_number}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (user?.phone_number) {
                                window.open(`tel:${user.phone_number}`);
                              }
                            }}
                            sx={{
                              color: user?.phone_number
                                ? reduxTheme.primary_colors[400]
                                : reduxTheme.primary_colors[800],
                              opacity: user?.phone_number ? 1 : 0.5,
                            }}
                          >
                            <Call fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Email */}
                        <Tooltip title={user.email || "No email address"}>
                          <IconButton
                            disabled={!user.email}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (user.email) {
                                window.open(`mailto:${user.email}`);
                              }
                            }}
                            sx={{
                              color: user.email
                                ? reduxTheme.primary_colors[400]
                                : reduxTheme.primary_colors[800],
                              opacity: user.email ? 1 : 0.5,
                            }}
                          >
                            <Email fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <Box sx={{ width: "100%" }}>
                        <Button
                          fullWidth
                          variant={
                            isCurrentAssignee && isAssigned
                              ? "outlined"
                              : "contained"
                          }
                          onClick={() => handleAssign(user)}
                          disabled={
                            assigningUserId !== null ||
                            !collectedComments ||
                            (isAssigned && isCurrentAssignee)
                          }
                          color={
                            isCurrentAssignee && isAssigned
                              ? "success"
                              : "primary"
                          }
                        >
                          {assigningUserId === user.id ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : isCurrentAssignee && isAssigned ? (
                            "ASSIGNED"
                          ) : (
                            "ASSIGN"
                          )}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 2 }}>
        <Button onClick={handleClose}>CANCEL</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignMemoryBookModal;
